import {
    type CSSProperties,
    type FC,
    Fragment,
    useContext,
} from "react";
import { currency } from "../../../Utilities/Generic/Numbers";
import { createBorderStyles } from "../../Forms/DragDrop/BorderSettingsDialog";
import { DragDropComponentEmptyContext, DragDropComponentErrorContext } from "../../Forms/DragDrop/DragDropComponent";
import { DragDropDataContext } from "../../Forms/DragDrop/DragDropRenderer";
import { type ComponentOptions } from "../../Forms/DragDrop/DragDropTypes";
import { hasColumnsValue } from "../../Forms/DragDrop/ReorderableTableSettings";
import { createStyleFromPadding } from "../../Forms/PaddingInput";
import NoTranslate from "../../NoTranslate";

export const ReceiptGiftCardsComponent: FC<ComponentOptions> = ({
    style,
    value,
}) => {
    const { giftCards } = useContext(DragDropDataContext);

    const error = useContext(DragDropComponentErrorContext);
    const empty = useContext(DragDropComponentEmptyContext);

    if(!hasColumnsValue(value)) {
        return error;
    }

    const { columns } = value;

    // Check to see if all columns are disabled
    let allDisabled = true;
    let lastColumn = 0;

    for(let i = 0, l = columns.length; i < l; i++) {
        if(columns[i].enabled) {
            lastColumn = i;
            allDisabled = false;
        }
    }

    if(allDisabled) {
        return empty;
    }

    const { border, fontSize, padding, ...remainingStyle } = style;

    const wrapperStyle: CSSProperties = {
        width: "100%",
        ...(border ? createBorderStyles(border) : {}),
        ...(padding ? createStyleFromPadding(padding) : {}),
    };

    const tableStyle: CSSProperties = {
        tableLayout: "auto",
        width      : "100%",
        fontSize   : `${fontSize}px`,
        ...remainingStyle,
    };

    if(!Array.isArray(giftCards) || !giftCards.length) {
        return empty;
    }

    return (
        <div style={wrapperStyle}>
            <table style={tableStyle}>
                <tbody>
                    {giftCards.map(giftCard => {
                        const dStyle: CSSProperties = {
                            border       : "none",
                            paddingTop   : 0,
                            paddingBottom: 0,
                            textAlign    : "right",
                        };

                        return (
                            <Fragment key={giftCard.code}>
                                <tr>
                                    <td style={{ ...dStyle, paddingLeft: "0" }}>Gift Card</td>
                                    <td style={{ ...dStyle, paddingRight: "0", width: "6em", wordBreak: "break-all" }}>
                                        {giftCard.code}
                                    </td>
                                </tr>
                                {columns.map((col, index) => {
                                    if(!col.enabled) {
                                        return;
                                    }

                                    const tdStyle = { ...dStyle };

                                    if(lastColumn === index) {
                                        tdStyle.paddingBottom = "0.5em";
                                    }

                                    const key = col.column;

                                    if(!(key in giftCard)) {
                                        return;
                                    }

                                    return (
                                        <tr
                                            key={col.column}
                                            className={col.column}
                                        >
                                            <td style={{ ...tdStyle, paddingLeft: "0" }}>{col.name}</td>
                                            <td style={{ ...tdStyle, paddingRight: "0", width: "6em" }}>
                                                {key === "expiry" ? (
                                                    giftCard[key]
                                                ) : (
                                                    <NoTranslate>{currency(giftCard[key] as number)}</NoTranslate>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
